'use client';

import React, { ReactNode, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';

import { LAYERS } from '@/shared/components/ui/layers';
import { cn } from '@/shared/utils/cn';

type ModalProps = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string; // classes du panneau
  overlayClassName?: string; // classes de l’overlay
  closeOnEsc?: boolean; // défaut: true
  closeOnOverlayClick?: boolean; // défaut: true
  ariaLabel?: string; // si pas de Header/aria-labelledby
  labelledById?: string; // id du titre si fourni par l’appelant
};

export function Modal({
  open,
  onClose,
  children,
  className,
  overlayClassName,
  closeOnEsc = true,
  closeOnOverlayClick = true,
  ariaLabel,
  labelledById,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // ESC pour fermer
  useEffect(() => {
    if (!open || !closeOnEsc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, closeOnEsc, onClose]);

  // Clic overlay pour fermer
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (!closeOnOverlayClick) return;
      // ferme seulement si clique en dehors du panneau
      if (e.target === e.currentTarget) onClose();
    },
    [closeOnOverlayClick, onClose],
  );

  // Focus auto sur le panneau à l’ouverture
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div
          className={cn(
            /*
              `pointer-events-auto` : un `Dialog` Radix ouvert — un panneau latéral — pose
              `pointer-events: none` sur le `body` et ne le rend qu'à sa propre couche. Une modale
              ouverte depuis un panneau s'affichait alors sans répondre à rien (#1891).
            */
            'pointer-events-auto fixed inset-0 flex items-center justify-center',
            LAYERS.modal,
            overlayClassName ?? 'bg-black/40 backdrop-blur-[1px]',
          )}
          onMouseDown={handleOverlayClick}
          aria-hidden={false}
          /*
            **La marque d'une couche de modale** (#2148).

            Un panneau latéral non modal ne confisque plus le clic extérieur : sans cette marque, un
            clic dans la modale serait vu comme un clic « dehors » et refermerait le panneau sous
            elle — c'est-à-dire pendant qu'on s'en sert.
          */
          data-modal-layer=""
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelledById}
            aria-label={labelledById ? undefined : ariaLabel}
            ref={panelRef}
            tabIndex={-1}
            initial={{ scale: 0.98, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.98, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className={cn(
              'w-full max-w-lg rounded-xl bg-white shadow-lg outline-none',
              className ?? 'p-0',
            )}
            onMouseDown={e => e.stopPropagation()} // empêche la fermeture si on clique dedans
          >
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

/* Slots simples pour structurer le contenu */
Modal.Header = function ModalHeader({
  children,
  id,
  className,
}: {
  children: ReactNode;
  id?: string;
  className?: string;
}) {
  return (
    <div className={cn('border-b px-6 py-4', className)} id={id}>
      {children}
    </div>
  );
};

Modal.Body = function ModalBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('px-6 py-6', className)}>{children}</div>;
};

Modal.Footer = function ModalFooter({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex justify-end gap-3 border-t px-6 py-4', className)}>{children}</div>
  );
};
